import { SortEnumType } from "@/gql/graphql";
import type {
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridSortDirection,
  GridSortModel,
} from "@mui/x-data-grid";
import cleanDeep from "clean-deep";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { GridGetRowsParams } from "@mui/x-data-grid-premium";
dayjs.extend(utc);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const setNestedRecordValue = (
  root: Record<string, unknown>,
  path: readonly string[],
  value: unknown
) => {
  let current = root;

  for (let i = 0; i < path.length; i++) {
    const key = path[i];
    if (key === undefined) {
      return;
    }

    const isLeaf = i === path.length - 1;
    if (isLeaf) {
      current[key] = value;
      return;
    }

    const existing = current[key];
    const next = isRecord(existing) ? existing : {};
    if (!isRecord(existing)) {
      current[key] = next;
    }
    current = next;
  }
};

const splitNestedField = (
  field: string
): { parentField: string; childField: string } | undefined => {
  const segments = field.split(".");
  const parentField = segments[0];
  const childField = segments[1];

  if (parentField === undefined || childField === undefined) {
    return undefined;
  }

  return { parentField, childField };
};

/**
 * This module provides utility functions to convert MUI Data Grid models
 * to GraphQL compatible formats.
 *
 * NOTE: please do not put functions related to specific tables (e.g. Invoices)
 * in this file. This file should only contain generic conversion functions.
 */

const mapGridSortToGql = (sort: GridSortDirection) => {
  if (sort === "asc") return SortEnumType.Asc;
  if (sort === "desc") return SortEnumType.Desc;
};

/**
 * Converts the sort model from MUI Data Grid to a GraphQL compatible format.
 */
const order = (sortModel: GridSortModel) =>
  sortModel.map(({ field, sort }) => {
    const nested: Record<string, unknown> = {};
    setNestedRecordValue(nested, field.split("."), mapGridSortToGql(sort));
    return nested;
  });

/**
 * Converts the filter model from MUI Data Grid to a GraphQL compatible format.
 */
const filterModel = (
  model: GridFilterModel,
  quickFilterableFields: string[],
  exactMatchFields: string[] = []
) => {
  // The quick-filter search and the column filters always narrow together (AND).
  // The column items combine by the panel's And/Or, scoped to themselves only.
  // Only add a clause/group when it actually contains a filter, so we never emit
  // empty `{}` / `{ and: [] }` groups into the `where` structure.
  const andClauses: Record<string, unknown>[] = [];

  if (model.quickFilterValues && model.quickFilterValues.length > 0) {
    const quickFilterClause = gridToGql.quickFilter(
      model.quickFilterValues,
      quickFilterableFields,
      exactMatchFields
    );
    if (Object.keys(quickFilterClause).length > 0) {
      andClauses.push(quickFilterClause);
    }
  }

  const columnFilterClauses = gridToGql.columnFilters(model.items);
  if (columnFilterClauses.length > 0) {
    const columnLogicOperator = model.logicOperator ?? "and";
    andClauses.push({ [columnLogicOperator]: columnFilterClauses });
  }

  return where({ and: andClauses });
};

const parseQuickFilterExactLong = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
};

const mergeOrFilterObjects = (
  ...filters: Record<string, unknown>[]
): Record<string, unknown> => {
  const or: Record<string, unknown>[] = [];
  for (const filter of filters) {
    if (Array.isArray(filter.or)) {
      or.push(...(filter.or as Record<string, unknown>[]));
    }
  }
  return or.length > 0 ? { or } : {};
};

/**
 * Converts the quick filter values from MUI Data Grid to a GraphQL compatible format.
 * String fields use `contains`. Optional `exactMatchFields` use `eq` when the search term is numeric.
 */
const quickFilter = (
  quickFilterValues: GridFilterModel["quickFilterValues"],
  filterableFields: string[],
  exactMatchFields: string[] = []
) => {
  const filterString = quickFilterValues?.join(" ");

  if (!filterString) {
    return {};
  }

  const containsFilter = dotListToOrObject(filterableFields, {
    contains: filterString,
  });

  const exactMatchValue = parseQuickFilterExactLong(filterString);
  if (exactMatchValue === undefined || exactMatchFields.length === 0) {
    return containsFilter;
  }

  const exactMatchFilter = dotListToOrObject(exactMatchFields, {
    eq: exactMatchValue,
  });

  return mergeOrFilterObjects(containsFilter, exactMatchFilter);
};

/**
 * Converts a list of dot-separated paths into a nested GraphQL "or" filter object.
 */
const dotListToOrObject = (paths: string[], leafValue: unknown) => {
  // Helper to insert a path into the result object
  function insert(obj: Record<string, unknown>, parts: string[]) {
    const [head, ...rest] = parts;
    if (head === undefined) {
      return;
    }

    // If this is the last part, add the leaf value
    if (rest.length === 0) {
      if (!Array.isArray(obj.or)) {
        obj.or = [];
      }
      (obj.or as Record<string, unknown>[]).push({
        [head]: leafValue,
      });
      return;
    }

    // Otherwise, go deeper into the object
    let child: Record<string, unknown> | undefined = Array.isArray(obj.or)
      ? (obj.or as Record<string, unknown>[]).find((item) => item[head])
      : undefined;

    if (!child) {
      child = { [head]: {} };
      if (!Array.isArray(obj.or)) {
        obj.or = [];
      }
      (obj.or as Record<string, unknown>[]).push(child);
    }

    const childRecord = child[head];
    if (isRecord(childRecord)) {
      insert(childRecord, rest);
    }
  }

  // Build the result object by inserting each path
  const result: Record<string, unknown> = {};
  for (const path of paths) {
    const parts = path.split(".");
    insert(result, parts);
  }
  return result;
};

/**
 * Converts the pagination model from MUI Data Grid to a GraphQL compatible format.
 */
const pages = (paginationModel: GridPaginationModel) => {
  const { page, pageSize } = paginationModel;
  return {
    first: pageSize,
    // "after" cursor is just a base64 encoded page number
    after: btoa((pageSize * page - 1).toString()),
  };
};

/**
 * Generates a continuous pagination object for GraphQL queries.
 */
const continuous = (start: number, end: number) => ({
  first: end - start + 1,
  after: btoa((start - 1).toString()),
});

const DATE_FIELDS = ["createdDate", "dueDate"];

const getDateRange = (dateValue: Date | string) => {
  // Parse and calculate dates in UTC to avoid timezone issues
  const date = dayjs.utc(dateValue);
  return {
    startOfDay: date.startOf("day").toISOString(),
    endOfDay: date.endOf("day").toISOString(),
  };
};

/**
 * Handles date field filters by converting date-only comparisons to ignore time component.
 * TODO: Fix so it doesn't require hardcoding date fields.
 */
const handleDateFilter = (
  field: string,
  operator: GridFilterItem["operator"],
  value: unknown
) => {
  if (!DATE_FIELDS.includes(field)) {
    return null;
  }

  const isDateValue =
    value instanceof Date ||
    (typeof value === "string" && dayjs(value).isValid());

  if (!isDateValue) {
    return null;
  }

  const { startOfDay, endOfDay } = getDateRange(value);
  switch (operator) {
    case "equals":
    case "is":
    case "=":
      return {
        and: [{ [field]: { gte: startOfDay } }, { [field]: { lte: endOfDay } }],
      };

    case "notEquals":
    case "isNot":
    case "is not":
    case "not":
    case "doesNotEqual":
    case "!=":
      return {
        or: [{ [field]: { lt: startOfDay } }, { [field]: { gt: endOfDay } }],
      };

    case "greaterThan":
    case "isAfter":
    case "after":
    case ">":
      return { [field]: { gt: endOfDay } };

    case "greaterThanOrEqual":
    case "onOrAfter":
    case ">=":
      return { [field]: { gte: startOfDay } };

    case "lessThan":
    case "before":
    case "<":
      return { [field]: { lt: startOfDay } };

    case "lessThanOrEqual":
    case "onOrBefore":
    case "<=":
      return { [field]: { lte: endOfDay } };

    case "endsWith":
    case "startsWith":
    case "contains":
    case "doesNotContain":
    case "isEmpty":
    case "isNotEmpty":
    case "isAnyOf":
    default:
      return null;
  }
};

/**
 * Converts MUI DataGrid column filters to GraphQL filter format.
 * Handles all MUI DataGrid filter operators.
 * For date fields, converts date-only comparisons to ignore time component.
 */
const columnFilters = (filterItems: GridFilterModel["items"]) => {
  return filterItems
    .filter((item) => {
      // isEmpty/isNotEmpty carry no value but still map to a null check.
      if (item.operator === "isEmpty" || item.operator === "isNotEmpty") {
        return true;
      }
      return (
        item.value !== undefined && item.value !== null && item.value !== ""
      );
    })
    .map((item) => {
      const field = item.field;
      const value: unknown = item.value;
      const operator = item.operator;

      const dateFilter = handleDateFilter(field, operator, value);
      if (dateFilter) {
        return dateFilter;
      }

      // Handle nested fields like "contract.customerName" -> { contract: { customerName: { ... } } }
      const nestedField = splitNestedField(field);
      if (nestedField) {
        const { parentField, childField } = nestedField;
        const filterValue = getFilterValue(operator, value);
        return { [parentField]: { [childField]: filterValue } };
      }

      const filterValue = getFilterValue(operator, value);
      return { [field]: filterValue };
    })
    .filter((filter) => Object.keys(filter).length > 0);
};

const getFilterValue = (
  operator: GridFilterItem["operator"],
  value: unknown
) => {
  switch (operator) {
    case "equals":
    case "is":
    case "=":
      return { eq: value };
    case "notEquals":
    case "isNot":
    case "is not":
    case "not":
    case "doesNotEqual":
    case "!=":
      return { neq: value };
    case "contains":
      return { contains: value };
    case "doesNotContain":
      return { ncontains: value };
    case "startsWith":
    case "starts with":
      return { startsWith: value };
    case "endsWith":
    case "ends with":
      return { endsWith: value };
    case "does not start with":
      return { nstartsWith: value };
    case "does not end with":
      return { nendsWith: value };
    case "greaterThan":
    case "isAfter":
    case "after":
    case ">":
      return { gt: value };
    case "greaterThanOrEqual":
    case "onOrAfter":
    case ">=":
      return { gte: value };
    case "lessThan":
    case "before":
    case "<":
      return { lt: value };
    case "lessThanOrEqual":
    case "onOrBefore":
    case "<=":
      return { lte: value };
    case "isAnyOf":
    case "is any of":
      return { in: value };
    case "isNotAnyOf":
    case "is not any of":
      return { nin: value };

    // where() keeps these nulls (nullValues: false) so they reach the backend.
    case "isEmpty":
      return { eq: null };
    case "isNotEmpty":
      return { neq: null };

    default:
      return {};
  }
};

const dataSourceToGql = <
  TVariables extends {
    after?: unknown;
    first?: unknown;
    order?: unknown;
    where?: unknown;
  },
>(
  params: GridGetRowsParams,
  pagingType: "pagination" | "infinite" = "pagination",
  quickFilterableFields: string[]
): Pick<TVariables, "after" | "first" | "order" | "where"> => {
  const { paginationModel, sortModel, filterModel, start, end } = params;

  const where = gridToGql.filterModel(filterModel, quickFilterableFields);
  const order = gridToGql.order(sortModel);

  if (pagingType === "infinite") {
    const { first, after } = gridToGql.continuous(Number(start), end);

    return {
      first,
      after,
      where,
      order,
    };
  }

  if (paginationModel) {
    const { first, after } = pages(paginationModel);
    return {
      first,
      after,
      where,
      order,
    };
  }

  return {
    where,
    order,
  };
};

/**
 * Cleans the where clause (drops empty objects/arrays/strings and `undefined`).
 * Keeps `null` so isEmpty/isNotEmpty null checks survive, so callers must use
 * `undefined`, not `null`, to mean "no filter".
 */
const where = <T>(where: T) => {
  const cleanedWhere = cleanDeep(where, { nullValues: false });
  return Object.keys(cleanedWhere).length ? cleanedWhere : undefined;
};

export const gridToGql = {
  order,
  quickFilter,
  columnFilters,
  pages,
  continuous,
  where,
  filterModel,
  dotListToOrObject,
  dataSourceToGql,
};
